import { format } from 'date-fns';
import { Link } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { GlobalRecentSubset } from '@/api/types/backend-dto-types';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { cn } from '@/new/lib/utils';

export function WhereYouLeftOff({ recentSubsets }: { recentSubsets: GlobalRecentSubset[] }) {
  if (recentSubsets.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <Chip className={cn('mb-1.5 py-1 text-gray-500')}>
        <Icon className="lucide--history" />
        Where you left off
      </Chip>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {recentSubsets.map((subset) => {
          const isAllDocuments = subset.case.isAllDocuments;
          const subPath = isAllDocuments ? '' : `/subsets/${subset.caseId}`;
          const linkTo = `/matters/${subset.case.matterId}${subPath}`;

          return (
            <Link
              key={subset.caseId}
              to={linkTo}
              className="basis-1/3"
              onClick={() =>
                trackCustomEvent(TrackingEventsV2.NavigationClickRecentSubset, {
                  matterId: subset.case.matterId,
                  subsetId: subset.caseId,
                })
              }
            >
              <div className="group relative h-full rounded-md border border-gray-200 bg-gray-50 px-4.5 py-3 duration-150 hover:border-gray-300">
                <div className="flex items-center justify-between gap-5">
                  <div className="shrink overflow-ellipsis">
                    <div className="mb-1 flex items-center gap-1.5">
                      <Icon className="lucide--briefcase text-sm text-teal-500" />
                      <h3 className="line-clamp-1 text-sm font-medium break-all text-gray-900">
                        {subset.case.matter.name}
                      </h3>
                    </div>

                    <div className="line-clamp-1 flex items-center gap-1.5 text-xs text-gray-500">
                      {isAllDocuments ? (
                        <>
                          <span>Matter</span>
                        </>
                      ) : (
                        <>
                          <Icon className="lucide--group text-sky-500" />
                          <span className="line-clamp-1 break-all">{subset.case.name}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <Icon className="lucide--history text-xs text-gray-400" />
                    <span className="text-xs text-gray-500">{format(subset.accessedAt, 'dd MMMM')}</span>
                    <Icon className="lucide--arrow-right text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-600" />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
